"use client";

import React, { useRef, useState, useMemo } from "react";
import { motion, useInView } from "framer-motion";
import Image from "next/image";
import SmoothImage from "@/components/SmoothImage";
import { renderTextWithBreaks } from "@/utils/stripHTML";
import { cn } from "@/utils/cn";

export interface DirectionBadgeItem {
  id: string;
  title: string;
  subtitle: string;
  top: string;
  left: string;
  rotation: number;
  arrowPosition: "right" | "left" | "top" | "bottom";
}

export interface LocationData {
  sectionIndex?: string;
  totalSections?: string;
  tag: string;
  connectTitle1: string;
  connectTitleScript: string;
  connectTitle2: string;
  connectDesc: string;
  mapImage?: string;
  projectPin?: {
    top: string;
    left: string;
  };
  directionBadges?: DirectionBadgeItem[];
}

interface LocationHighlightSectionProps {
  data: LocationData;
}

/* ========================================================================= */
/* CONCENTRIC ROTATING RADIUS RINGS (2km, 5km, 10km) & PROJECT LOCATION PIN */
/* ========================================================================= */
/* ========================================================================= */
/* CONCENTRIC ROTATING RADIUS RINGS (2km, 5km, 10km) & PROJECT LOCATION PIN */
/* ========================================================================= */
const ProjectLocationPinBadge = ({
  top = "44%",
  left = "40%",
}: {
  top?: string;
  left?: string;
}) => {
  return (
    <div
      className="absolute pointer-events-none select-none z-25"
      style={{ top, left }}
    >
      {/* 3 THIN DASHED CONCENTRIC RINGS (RINGS ROTATE, TEXT REMAINS STATIC) */}
      <div
        className="absolute pointer-events-none select-none z-10"
        style={{
          top: "0px",
          left: "0px",
          transform: "translate(-50%, -50%)",
        }}
      >
        <svg
          viewBox="0 0 500 500"
          className="w-[280px] h-[280px] sm:w-[420px] sm:h-[420px] md:w-[560px] md:h-[560px] overflow-visible"
        >
          <defs>
            <path
              id="ring-2km-path"
              d="M 250,250 m -65,0 a 65,65 0 1,1 130,0 a 65,65 0 1,1 -130,0"
            />
            <path
              id="ring-5km-path"
              d="M 250,250 m -125,0 a 125,125 0 1,1 250,0 a 125,125 0 1,1 -250,0"
            />
            <path
              id="ring-10km-path"
              d="M 250,250 m -195,0 a 195,195 0 1,1 390,0 a 195,195 0 1,1 -390,0"
            />
          </defs>

          {/* Ring 1: 2km (Thin Circle Rotates Clockwise) */}
          <motion.circle
            cx="250"
            cy="250"
            r="65"
            fill="none"
            stroke="#54463F"
            strokeWidth="0.8"
            strokeDasharray="4 4"
            opacity="0.65"
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "250px 250px" }}
          />

          {/* Ring 2: 5km (Thin Circle Rotates Counter-Clockwise) */}
          <motion.circle
            cx="250"
            cy="250"
            r="125"
            fill="none"
            stroke="#54463F"
            strokeWidth="0.8"
            strokeDasharray="5 5"
            opacity="0.55"
            animate={{ rotate: -360 }}
            transition={{ duration: 45, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "250px 250px" }}
          />

          {/* Ring 3: 10km (Thin Circle Rotates Clockwise) */}
          <motion.circle
            cx="250"
            cy="250"
            r="195"
            fill="none"
            stroke="#54463F"
            strokeWidth="0.8"
            strokeDasharray="6 6"
            opacity="0.5"
            animate={{ rotate: 360 }}
            transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
            style={{ transformOrigin: "250px 250px" }}
          />

          {/* STATIC TEXT LABELS (NO ROTATION) */}
          <text
            fill="color-third"
            fontSize="10"
            fontWeight="bold"
            opacity="0.9"
          >
            <textPath href="#ring-2km-path" startOffset="12%">
              2 km
            </textPath>
          </text>
          <text
            fill="color-third"
            fontSize="11"
            fontWeight="bold"
            opacity="0.85"
          >
            <textPath href="#ring-5km-path" startOffset="12%">
              5 km
            </textPath>
          </text>
          <text
            fill="color-third"
            fontSize="12"
            fontWeight="bold"
            opacity="0.8"
          >
            <textPath href="#ring-10km-path" startOffset="12%">
              10 km
            </textPath>
          </text>
        </svg>
      </div>

      {/* SUBTLE BLINKING CIRCLE INDICATOR AT PIN APEX */}
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 z-30 flex items-center justify-center pointer-events-none">
        {/* Subtle Outer Pulsing Aura Wave */}
        <motion.div
          animate={{ scale: [1, 1.5, 1], opacity: [0.55, 0, 0.55] }}
          transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          className="w-4 h-4 rounded-full bg-third/30 border border-third/70 absolute"
        />
        {/* Gentle Inner Blinking Dot */}
        <motion.div
          animate={{ opacity: [1, 0.55, 1], scale: [1, 1.08, 1] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          className="w-3 h-3 rounded-full bg-third border-[1.5px] border-white shadow-[0_0_6px_rgba(238,149,54,0.7)] relative z-10"
        />
      </div>

      {/* COMPACT CIRCULAR LOGO BADGE & NEEDLE POINTER (THIRD COLOR: third) */}
      <div
        className="absolute z-20 flex items-center pointer-events-auto cursor-pointer"
        style={{
          top: "0px",
          left: "0px",
          transform: "translate(-100%, -50%)",
        }}
      >
        <div className="relative flex items-center">
          {/* Reduced Circular Badge Card */}
          <div className="w-14 h-14 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white border-[2.5px] border-third shadow-[0_6px_20px_rgba(0,0,0,0.25)] flex flex-col items-center justify-center p-1.5 sm:p-2 z-20 relative overflow-hidden">
            {/* Inner Accent Ring */}
            <div className="absolute inset-1 rounded-full border border-third/40 pointer-events-none" />
            <img
              src="/logos/logo_transparent.png"
              alt="Dragon Home"
              className="w-10 sm:w-14 md:w-18 h-auto object-contain brightness-0 saturate-100 invert-[20%] sepia-[20%] hue-rotate-[140deg] filter"
            />
          </div>

          {/* Pointer Beak / Needle SVG Connecting Badge Circle to Apex Tip */}
          <svg
            viewBox="0 0 100 40"
            className="w-10 sm:w-14 md:w-16 h-5 sm:h-7 text-third -ml-2 z-10 filter drop-shadow-[0_3px_5px_rgba(0,0,0,0.2)]"
          >
            <path
              d="M0,5 Q50,15 100,20 Q50,25 0,35 Z"
              fill="#EFECE6"
              stroke="#54463F"
              strokeWidth="2"
            />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default function LocationHighlightSection({
  data,
}: LocationHighlightSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });

  if (!data) return null;

  const {
    tag = "05 · VỊ TRÍ & KẾT NỐI",
    connectTitle1,
    connectTitleScript,
    connectTitle2,
    connectDesc,
    mapImage = "/images/Map_8.png",
    projectPin = { top: "45%", left: "46%" },
    directionBadges = [],
  } = data;

  return (
    <section
      ref={sectionRef}
      id="vi-tri-ket-noi"
      className="relative w-full h-auto lg:h-screen lg:min-h-screen lg:max-h-screen flex flex-col lg:flex-row items-center justify-start lg:justify-between overflow-hidden text-primaryDark select-none pt-14 sm:pt-20 md:pt-22 lg:pt-24 pb-4 sm:pb-8 lg:pb-8"
    >
      {/* PAPER TEXTURE BACKGROUND */}

      <div className="absolute inset-0 z-0 pointer-events-none">
        <Image
          src="/images/background_2.png"
          alt="Paper Texture Background"
          fill
          sizes="100vw"
          priority
          className="object-cover object-center"
        />
      </div>

      {/* LEFT DECORATIVE SHADOW OVERLAY IMAGE */}
      <div className="absolute inset-y-0 left-0 h-full pointer-events-none z-[2] opacity-100 flex items-end justify-start">
        <img
          src="/images/deco_shadow_left_bottom.png"
          alt="Decorative Shadow Overlay"
          className="h-full w-auto object-contain object-left-bottom"
        />
      </div>

      <div className="flex flex-col lg:flex-row items-center justify-start lg:justify-between relative w-full h-auto lg:h-full gap-y-3 sm:gap-y-4 lg:gap-y-0 z-10">
        {/* Left Column: Title, Sub-desc, Timeline */}
        <div className="w-full lg:w-[38%] xl:w-[40%] min-h-[auto] lg:h-full flex flex-col justify-center px-6 sm:px-10 lg:pl-[7%] xl:pl-[9%] lg:pr-4 flex-shrink-0 z-10 pt-2 lg:pt-4">
          {/* Tag */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-3"
          >
            <span className="w-8 h-[1px] bg-primaryDark/40" />
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-primaryDark/70 font-sans">
              {tag}
            </span>
          </motion.div>

          {/* Title */}
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-lora font-normal text-primaryDark mb-4 sm:mb-6 tracking-tight title-leading"
            style={{ lineHeight: 1.15 }}
          >
            {renderTextWithBreaks(connectTitle1)}{" "}
            <span className="font-serif italic font-normal text-[#8A724C]">
              {renderTextWithBreaks(connectTitleScript)}
            </span>{" "}
            {renderTextWithBreaks(connectTitle2)}
          </motion.h2>

          {/* Description */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-primaryDark/85 font-light font-sans text-xs sm:text-sm leading-relaxed mb-2 sm:mb-4 lg:mb-8 max-w-none lg:max-w-[700px] text-justify"
          >
            {renderTextWithBreaks(connectDesc)}
          </motion.p>
        </div>

        {/* Right Column: Map Image with Dynamic Pins & Animations (ACCORDING TO 1082x1000 ASPECT RATIO) */}
        <div className="w-full lg:w-[56%] xl:w-[60%] py-0 sm:py-2 z-0 overflow-visible relative flex items-center justify-center lg:justify-start flex-shrink-0 mb-2 sm:mb-4 lg:mb-0">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="w-[78%] sm:w-[82%] md:w-[86%] lg:w-full max-w-[420px] lg:max-w-none aspect-[1082/1000] lg:max-h-[84vh] relative overflow-visible -ml-8 sm:ml-0 lg:ml-0"
          >
            {/* Base Map */}
            <SmoothImage
              src={mapImage}
              alt="Map connection"
              fill
              sizes="(max-width: 1024px) 85vw, 60vw"
              priority
              className="w-full h-full object-contain z-10 relative"
            />

            {/* PROJECT LOCATION PIN BADGE WITH 3 ROTATING RADIUS RINGS (2km, 5km, 10km) */}
            <ProjectLocationPinBadge
              top={projectPin?.top || "45%"}
              left={projectPin?.left || "46%"}
            />

            {/* 5 ANIMATED DIRECTION BADGES (ORANGE BADGES WITH MOVING CHEVRON ARROWS) */}
            {directionBadges.map((badge) => {
              const flexClass =
                badge.arrowPosition === "left"
                  ? "flex-row-reverse items-center justify-center"
                  : badge.arrowPosition === "top"
                    ? "flex-col-reverse items-center justify-center"
                    : badge.arrowPosition === "bottom"
                      ? "flex-col items-center justify-center"
                      : "flex-row items-center justify-center";

              const arrowMarginClass =
                badge.arrowPosition === "right"
                  ? "-ml-2 sm:-ml-2.5 my-auto"
                  : badge.arrowPosition === "left"
                    ? "-mr-2 sm:-mr-2.5 my-auto"
                    : badge.arrowPosition === "bottom"
                      ? "mt-1.5 sm:mt-2 mx-auto"
                      : badge.arrowPosition === "top"
                        ? "mb-1.5 sm:mb-2 mx-auto"
                        : "my-auto";

              return (
                <div
                  key={badge.id}
                  className={`absolute z-30 flex gap-1 sm:gap-1.5 pointer-events-none select-none ${flexClass}`}
                  style={{
                    top: badge.top,
                    left: badge.left,
                  }}
                >
                  {/* Solid Direction Badge with Third Accent Tone (third) */}
                  <div className="w-[82px] sm:w-[110px] md:w-[132px] bg-third text-white py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg shadow-md flex flex-col items-center justify-center text-center leading-tight flex-shrink-0 z-20 relative">
                    <span className="text-[6.5px] sm:text-[8px] md:text-[9px] font-sans font-semibold tracking-wider uppercase text-white/95 leading-none mb-0.5">
                      {badge.title}
                    </span>
                    <span className="text-[8px] sm:text-[10px] md:text-xs font-sans font-bold tracking-wide uppercase whitespace-nowrap text-white leading-tight">
                      {badge.subtitle}
                    </span>
                  </div>

                  {/* Seamless Animated Direction Chevrons (Underneath Badge Layer) */}
                  <div
                    className={`relative overflow-hidden w-9 sm:w-14 md:w-16 h-4 sm:h-6 flex items-center pointer-events-none select-none z-10 [mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_20%,black_80%,transparent_100%)] ${arrowMarginClass}`}
                    style={{
                      transform: `rotate(${badge.rotation}deg)`,
                    }}
                  >
                    <motion.div
                      className="flex items-center gap-[1px] absolute left-0"
                      animate={{
                        x: [-14, 0],
                      }}
                      transition={{
                        duration: 0.65,
                        repeat: Infinity,
                        ease: "linear",
                      }}
                    >
                      {[0, 1, 2, 3, 4, 5, 6, 7, 8].map((idx) => (
                        <svg
                          key={idx}
                          viewBox="0 0 10 14"
                          className="text-third fill-none stroke-current stroke-[3] flex-shrink-0 w-[9px] h-[13px] sm:w-[13px] sm:h-[17px]"
                        >
                          <path
                            d="M2 2l5 5-5 5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      ))}
                    </motion.div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
